import jsPDF from 'jspdf'

interface PicklistItem {
  id: string
  ic: string
  name: string
  quantity: number
  location_name?: string
  shelf_name?: string
  shelf_code?: string
  tote_name?: string
  tote_code?: string
}

interface HierarchicalPicklist {
  [locationName: string]: {
    [shelfName: string]: {
      [toteName: string]: PicklistItem[]
    }
  }
}

export function generatePicklistPDF(items: PicklistItem[], title: string = 'Pick List'): jsPDF {
  const pdf = new jsPDF({
    orientation: 'portrait',
    unit: 'in',
    format: 'letter' // 8.5" x 11"
  })

  // Organize items hierarchically
  const hierarchy: HierarchicalPicklist = {}

  items.forEach(item => {
    const location = item.location_name || 'Unassigned Location'
    const shelf = item.shelf_name || item.shelf_code || 'No Shelf'
    const tote = item.tote_name || item.tote_code || 'No Tote'

    if (!hierarchy[location]) hierarchy[location] = {}
    if (!hierarchy[location][shelf]) hierarchy[location][shelf] = {}
    if (!hierarchy[location][shelf][tote]) hierarchy[location][shelf][tote] = []

    hierarchy[location][shelf][tote].push(item)
  })

  let yPos = 0.75 // Start position from top
  const pageHeight = 10.5 // Letter height minus margins
  const lineHeight = 0.2

  // Header
  pdf.setFontSize(24)
  pdf.setFont('helvetica', 'bold')
  pdf.text(title, 0.5, yPos)
  yPos += 0.4

  pdf.setFontSize(10)
  pdf.setFont('helvetica', 'normal')
  pdf.text(`Generated: ${new Date().toLocaleString()}`, 0.5, yPos)
  pdf.text(`Total Items: ${items.length}`, 6.5, yPos)
  yPos += 0.3

  // Divider line
  pdf.setLineWidth(0.02)
  pdf.line(0.5, yPos, 8, yPos)
  yPos += 0.3

  // Iterate through hierarchy
  Object.entries(hierarchy).forEach(([location, shelves]) => {
    // Check if we need a new page
    if (yPos > pageHeight) {
      pdf.addPage()
      yPos = 0.75
    }

    // Location header
    pdf.setFontSize(14)
    pdf.setFont('helvetica', 'bold')
    pdf.setTextColor(0, 100, 200) // Blue
    pdf.text(`📍 ${location}`, 0.5, yPos)
    yPos += lineHeight + 0.1

    Object.entries(shelves).forEach(([shelf, totes]) => {
      if (yPos > pageHeight) {
        pdf.addPage()
        yPos = 0.75
      }

      // Shelf header
      pdf.setFontSize(12)
      pdf.setFont('helvetica', 'bold')
      pdf.setTextColor(100, 100, 100) // Gray
      pdf.text(`  🗄️ ${shelf}`, 0.7, yPos)
      yPos += lineHeight + 0.05

      Object.entries(totes).forEach(([tote, toteItems]) => {
        if (yPos > pageHeight) {
          pdf.addPage()
          yPos = 0.75
        }

        // Tote header
        pdf.setFontSize(11)
        pdf.setFont('helvetica', 'bold')
        pdf.setTextColor(80, 80, 80)
        pdf.text(`    📦 ${tote}`, 0.9, yPos)
        yPos += lineHeight

        // Items
        toteItems.forEach(item => {
          if (yPos > pageHeight) {
            pdf.addPage()
            yPos = 0.75
          }

          pdf.setFontSize(10)
          pdf.setFont('helvetica', 'normal')
          pdf.setTextColor(0, 0, 0)

          // Checkbox
          pdf.rect(1.1, yPos - 0.12, 0.12, 0.12)

          // IC code
          pdf.setFont('helvetica', 'bold')
          pdf.text(item.ic, 1.3, yPos)

          // Item name
          pdf.setFont('helvetica', 'normal')
          const itemName = item.name.length > 50 ? item.name.substring(0, 47) + '...' : item.name
          pdf.text(itemName, 2.5, yPos)

          // Quantity
          if (item.quantity > 1) {
            pdf.setFont('helvetica', 'bold')
            pdf.text(`Qty: ${item.quantity}`, 7.2, yPos)
          }

          yPos += lineHeight
        })

        yPos += 0.1 // Space after tote
      })

      yPos += 0.1 // Space after shelf
    })

    yPos += 0.2 // Space after location
  })

  // Footer on each page
  const pageCount = pdf.getNumberOfPages()
  for (let i = 1; i <= pageCount; i++) {
    pdf.setPage(i)
    pdf.setFontSize(8)
    pdf.setFont('helvetica', 'normal')
    pdf.setTextColor(150, 150, 150)
    pdf.text(`Page ${i} of ${pageCount}`, 7.5, 10.75)
    pdf.text('BGS Core Inventory System', 0.5, 10.75)
  }

  return pdf
}

export function printPicklist(pdf: jsPDF): void {
  const blob = pdf.output('blob')
  const url = URL.createObjectURL(blob)
  const printWindow = window.open(url, '_blank')

  if (printWindow) {
    printWindow.addEventListener('load', () => {
      printWindow.print()
    })
  }
}

export function downloadPicklist(pdf: jsPDF, filename: string = 'picklist.pdf'): void {
  pdf.save(filename)
}
